import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { usePortfolio } from "../hooks/usePortfolio";
import * as investmentService from "../services/investmentService";
import * as accountService from "../services/accountService";
import type {
  UserInvestment,
  TradingPerformanceRow,
  TradingPerformanceGranularity,
} from "../types/api";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { PortfolioPerformanceChart } from "../components/charts/PortfolioPerformanceChart";
import {
  TradingPerformanceChart,
  type TradingPerformanceChartPoint,
} from "../components/charts/TradingPerformanceChart";
import { useTranslation } from "react-i18next";
import { pickPackName } from "../utils/localeDisplay";
import { Link } from "react-router-dom";
import { Package, TrendingUp, BarChart3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const DashboardPage = () => {
  const { user } = useAuth();
  const { summary, loading, fetchSummary } = usePortfolio();
  const [range, setRange] = useState<"7J" | "30J" | "90J">("30J");
  const [investments, setInvestments] = useState<UserInvestment[]>([]);
  const [loadingInvestments, setLoadingInvestments] = useState(true);
  const [performanceGranularity, setPerformanceGranularity] =
    useState<TradingPerformanceGranularity>("day");
  const [tradingPerformance, setTradingPerformance] = useState<
    TradingPerformanceRow[]
  >([]);
  const [performanceSymbols, setPerformanceSymbols] = useState<string[]>([]);
  const [selectedPerformanceSymbol, setSelectedPerformanceSymbol] =
    useState<string>("");
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [chartPerformanceRows, setChartPerformanceRows] = useState<
    TradingPerformanceRow[]
  >([]);
  const [loadingChartPerformance, setLoadingChartPerformance] = useState(false);
  const [tradingPerfPage, setTradingPerfPage] = useState(1);
  const tradingPerfPerPage = 20;
  const { t, i18n } = useTranslation();

  const fetchTradingPerformance = useCallback(async () => {
    if (!user) return;
    setLoadingPerformance(true);
    try {
      const data = await accountService.getTradingPerformance(
        performanceGranularity,
        undefined,
        undefined,
        selectedPerformanceSymbol || undefined
      );
      setTradingPerformance(data.rows);
      setPerformanceSymbols(data.symbols);
    } catch (err) {
      console.error("Erreur chargement performances trading:", err);
      setTradingPerformance([]);
    } finally {
      setLoadingPerformance(false);
    }
  }, [user, performanceGranularity, selectedPerformanceSymbol]);

  useEffect(() => {
    if (user) void fetchTradingPerformance();
  }, [user, fetchTradingPerformance]);

  const fetchChartPerformance = useCallback(async () => {
    if (!user) return;
    setLoadingChartPerformance(true);
    try {
      const days = range === "7J" ? 7 : range === "30J" ? 30 : 90;
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - days + 1);
      const toStr = to.toISOString().slice(0, 10);
      const fromStr = from.toISOString().slice(0, 10);

      const data = await accountService.getTradingPerformance(
        "day",
        fromStr,
        toStr
      );
      setChartPerformanceRows(data.rows);
    } catch (err) {
      console.error("Erreur chargement données graphique:", err);
      setChartPerformanceRows([]);
    } finally {
      setLoadingChartPerformance(false);
    }
  }, [user, range]);

  useEffect(() => {
    if (user) void fetchChartPerformance();
  }, [user, fetchChartPerformance, range]);

  useEffect(() => {
    if (user) {
      void fetchSummary(user.id);
      const fetchInvestments = async () => {
        try {
          setLoadingInvestments(true);
          const data = await investmentService.getUserInvestments({
            limit: 3, // Afficher seulement les 3 premiers sur le dashboard
          });
          setInvestments(data);
        } catch (error) {
          console.error(
            "Erreur lors du chargement des investissements:",
            error
          );
        } finally {
          setLoadingInvestments(false);
        }
      };
      void fetchInvestments();
    }
  }, [fetchSummary, user]);

  const cards = [
    {
      label: t("dashboard.cardTotalValue"),
      value: summary ? `${summary.totalValue.toFixed(2)} $` : null,
    },
    {
      label: t("dashboard.cardInvested"),
      value: summary ? `${summary.totalInvested.toFixed(2)} $` : null,
    },
    {
      label: t("dashboard.cardPerformance"),
      value: summary ? `${summary.profitLoss.toFixed(2)} $` : null,
      accent:
        summary && summary.profitLoss >= 0
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-rose-600 dark:text-rose-400",
    },
  ];

  const performanceData = useMemo(() => {
    const totalInvested = summary?.totalInvested ?? 0;
    const limit = range === "7J" ? 7 : range === "30J" ? 30 : 90;
    const lang = i18n.language.slice(0, 2);

    const rowsByDay = new Map<string, { pnl: number }>();
    for (const row of chartPerformanceRows) {
      const dayKey = row.period.split("T")[0] ?? row.period;
      rowsByDay.set(dayKey, { pnl: row.pnl });
    }

    const result: { date: string; value: number }[] = [];
    let cumulative = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const toDayKey = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`;

    for (let i = limit - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dayKey = toDayKey(d);
      const row = rowsByDay.get(dayKey);
      if (row) cumulative += row.pnl;
      const value = totalInvested + cumulative;
      const dateLabel = d.toLocaleDateString(lang, {
        day: "numeric",
        month: "short",
      });
      result.push({ date: dateLabel, value: Math.round(value * 100) / 100 });
    }
    return result;
  }, [range, summary?.totalInvested, chartPerformanceRows, i18n.language]);

  const filters: ("7J" | "30J" | "90J")[] = ["7J", "30J", "90J"];

  const formatPeriodLabel = (
    period: string,
    granularity: TradingPerformanceGranularity
  ): string => {
    const lang = i18n.language.slice(0, 2);
    if (granularity === "year") return period;
    if (granularity === "month") {
      const [y, m] = period.split("-");
      const d = new Date(Number(y), Number(m) - 1, 1);
      return d.toLocaleDateString(lang, { month: "short", year: "numeric" });
    }
    if (granularity === "day") {
      const d = new Date(period + "T12:00:00");
      return d.toLocaleDateString(lang, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    if (granularity === "hour" && period.includes("T")) {
      const [datePart, hourPart] = period.split("T");
      const d = new Date(datePart + "T" + hourPart + ":00:00");
      return d.toLocaleString(lang, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return period;
  };

  const perfGranularities: TradingPerformanceGranularity[] = [
    "hour",
    "day",
    "month",
    "year",
  ];

  /** Données pour le graphique : ordre chronologique (ancien → récent) + P&L cumulé */
  const tradingPerformanceChartData =
    useMemo((): TradingPerformanceChartPoint[] => {
      if (tradingPerformance.length === 0) return [];
      const chronological = [...tradingPerformance].reverse();
      let cumulative = 0;
      return chronological.map((row) => {
        cumulative += row.pnl;
        return {
          period: row.period,
          periodLabel: formatPeriodLabel(row.period, performanceGranularity),
          gains: row.gains,
          losses: row.losses,
          pnl: row.pnl,
          tradesCount: row.tradesCount,
          cumulativePnl: cumulative,
        };
      });
    }, [tradingPerformance, performanceGranularity, i18n.language]);

  const totalTradingPerfPages = useMemo(
    () =>
      Math.max(1, Math.ceil(tradingPerformance.length / tradingPerfPerPage)),
    [tradingPerformance.length, tradingPerfPerPage]
  );

  useEffect(() => {
    if (tradingPerfPage > totalTradingPerfPages) {
      setTradingPerfPage(totalTradingPerfPages);
    }
  }, [tradingPerfPage, totalTradingPerfPages]);

  const paginatedTradingPerformance = useMemo(() => {
    const start = (tradingPerfPage - 1) * tradingPerfPerPage;
    return tradingPerformance.slice(start, start + tradingPerfPerPage);
  }, [tradingPerformance, tradingPerfPage, tradingPerfPerPage]);

  if (!user) {
    return <p>{t("dashboard.loginRequired")}</p>;
  }

  return (
    <section className="space-y-6 rounded-3xl border border-border bg-card p-6 text-foreground shadow-lg dark:border-white/10 dark:bg-white/5 dark:text-white dark:shadow-2xl">
      <div>
        <h2 className="text-2xl font-semibold">{t("dashboard.title")}</h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <article
            key={card.label}
            className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30"
            title={
              card.label === t("dashboard.cardPerformance")
                ? t("dashboard.cardPerformanceHelp")
                : undefined
            }>
            <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
              {card.label}
            </p>
            {loading || !card.value ? (
              <Skeleton className="mt-4 h-8 w-32" />
            ) : (
              <strong
                className={`mt-2 text-3xl font-semibold ${card.accent ?? ""}`}>
                {card.value}
              </strong>
            )}
            {card.label === t("dashboard.cardPerformance") && (
              <p className="mt-2 text-[10px] text-muted-foreground leading-tight dark:text-white/45">
                {t("dashboard.cardPerformanceHelp")}
              </p>
            )}
          </article>
        ))}
      </div>

      <div className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-muted-foreground dark:text-white/70">
              {t("dashboard.simulatedPerformanceTitle")}
            </p>
            <p className="text-xs text-muted-foreground dark:text-white/50">
              {t("dashboard.simulatedPerformanceSubtitle")}
            </p>
          </div>
          <div className="flex gap-2">
            {filters.map((filter) => (
              <Button
                key={filter}
                variant={range === filter ? "default" : "ghost"}
                className={
                  range === filter
                    ? ""
                    : "border border-border bg-muted/60 text-foreground hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                }
                onClick={() => setRange(filter)}>
                {filter}
              </Button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          {loadingChartPerformance ? (
            <Skeleton className="h-64 w-full" />
          ) : performanceData.length === 0 ? (
            <p className="flex h-64 items-center justify-center rounded-2xl border border-border bg-muted/40 text-sm text-muted-foreground dark:border-white/10 dark:bg-black/20 dark:text-white/50">
              {t(
                "dashboard.noChartData",
                "Aucune donnée de trading sur la période pour afficher le graphique."
              )}
            </p>
          ) : (
            <PortfolioPerformanceChart data={performanceData} />
          )}
        </div>
      </div>

      {/* Performances de trading par période */}
      <div className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                {t(
                  "dashboard.tradingPerformanceTitle",
                  "Performances de trading"
                )}
              </h3>
              <p className="text-xs text-muted-foreground dark:text-white/50">
                {t(
                  "dashboard.tradingPerformanceSubtitle",
                  "Gains et pertes agrégés par période"
                )}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground dark:text-white/40">
                {t("dashboard.tradingPerformanceHelp")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {performanceSymbols.length > 0 && (
              <Select
                value={selectedPerformanceSymbol || "all"}
                onValueChange={(v: string) =>
                  setSelectedPerformanceSymbol(v === "all" ? "" : v)
                }>
                <SelectTrigger className="w-[160px] border-border bg-background text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
                  <SelectValue
                    placeholder={t(
                      "dashboard.perfSymbolAll",
                      "Tous les symboles"
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("dashboard.perfSymbolAll", "Tous les symboles")}
                  </SelectItem>
                  {performanceSymbols.map((sym) => (
                    <SelectItem key={sym} value={sym}>
                      {sym}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {perfGranularities.map((g) => (
              <Button
                key={g}
                size="sm"
                variant={performanceGranularity === g ? "default" : "ghost"}
                className={
                  performanceGranularity === g
                    ? "bg-cyan-600 hover:bg-cyan-500"
                    : "border border-border bg-muted/60 text-foreground hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                }
                onClick={() => setPerformanceGranularity(g)}>
                {t(
                  `dashboard.perf${g.charAt(0).toUpperCase() + g.slice(1)}`,
                  g
                )}
              </Button>
            ))}
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border dark:border-white/10">
          {loadingPerformance ? (
            <div className="p-6">
              <Skeleton className="h-48 w-full rounded-xl" />
            </div>
          ) : tradingPerformance.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground dark:text-white/50">
              {t(
                "dashboard.noTradingData",
                "Aucune donnée de trading sur la période."
              )}
            </p>
          ) : (
            <>
              <div className="px-4 pt-4">
                <TradingPerformanceChart
                  data={tradingPerformanceChartData}
                  labels={{
                    gains: t("dashboard.perfGains", "Gains"),
                    losses: t("dashboard.perfLosses", "Pertes"),
                    pnl: t("dashboard.perfPnl", "P&L net"),
                    trades: t("dashboard.perfTrades", "Trades"),
                    cumulativePnl: t(
                      "dashboard.perfCumulativePnl",
                      "P&L cumulé"
                    ),
                  }}
                />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 dark:border-white/10 dark:bg-white/5">
                      <th className="px-4 py-3 font-semibold text-foreground dark:text-white/90">
                        {t("dashboard.perfPeriod", "Période")}
                      </th>
                      <th className="px-4 py-3 font-semibold text-emerald-600 dark:text-emerald-400/90 text-right">
                        {t("dashboard.perfGains", "Gains")}
                      </th>
                      <th className="px-4 py-3 font-semibold text-rose-600 dark:text-rose-400/90 text-right">
                        {t("dashboard.perfLosses", "Pertes")}
                      </th>
                      <th className="px-4 py-3 font-semibold text-foreground dark:text-white/90 text-right">
                        {t("dashboard.perfPnl", "P&L net")}
                      </th>
                      <th className="px-4 py-3 font-semibold text-muted-foreground dark:text-white/70 text-right">
                        {t("dashboard.perfTrades", "Trades")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTradingPerformance.map((row) => (
                      <tr
                        key={row.period}
                        className="border-b border-border transition-colors hover:bg-muted/50 dark:border-white/5 dark:hover:bg-white/5">
                        <td className="px-4 py-3 text-foreground dark:text-white/90">
                          {formatPeriodLabel(
                            row.period,
                            performanceGranularity
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600 dark:text-emerald-400">
                          +{row.gains.toFixed(2)} $
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-rose-600 dark:text-rose-400">
                          -{row.losses.toFixed(2)} $
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-semibold ${
                            row.pnl >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                          }`}>
                          {row.pnl >= 0 ? "+" : ""}
                          {row.pnl.toFixed(2)} $
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground dark:text-white/70">
                          {row.tradesCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground dark:border-white/10 dark:bg-black/40 dark:text-white/60">
                <span>
                  {t("dashboard.perfPageInfo", {
                    page: tradingPerfPage,
                    pages: totalTradingPerfPages,
                  })}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="border border-border bg-muted/60 text-foreground hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                    disabled={tradingPerfPage <= 1}
                    onClick={() =>
                      setTradingPerfPage((prev) => Math.max(1, prev - 1))
                    }>
                    {t("dashboard.prevPage", "Précédent")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="border border-border bg-muted/60 text-foreground hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                    disabled={tradingPerfPage >= totalTradingPerfPages}
                    onClick={() =>
                      setTradingPerfPage((prev) =>
                        Math.min(totalTradingPerfPages, prev + 1)
                      )
                    }>
                    {t("dashboard.nextPage", "Suivant")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Investissements actifs */}
      <div className="rounded-3xl border border-border bg-muted/30 p-4 dark:border-white/10 dark:bg-white/5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {t("dashboard.activeInvestments")}
          </h3>
          <Link
            to="/investments"
            className="text-sm text-cyan-700 hover:text-cyan-600 dark:text-cyan-400 dark:hover:text-cyan-300">
            {t("dashboard.viewAll")}
          </Link>
        </div>
        {loadingInvestments ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        ) : investments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4 dark:text-white/60">
            {t("dashboard.noActiveInvestments")}
          </p>
        ) : (
          <div className="space-y-3">
            {investments.slice(0, 3).map((investment) => {
              const pack = investment.pack;
              const displayName = pack
                ? pickPackName(pack, i18n.language)
                : "N/A";
              const bgGradient =
                pack?.primaryColor && pack?.secondaryColor
                  ? `linear-gradient(135deg, ${pack.primaryColor} 0%, ${pack.secondaryColor} 100%)`
                  : "linear-gradient(135deg, rgb(51 65 85 / 0.5) 0%, rgb(30 41 59 / 0.3) 100%)";

              return (
                <div
                  key={investment.id}
                  className="rounded-xl border border-border/50 p-3 backdrop-blur-sm dark:border-white/10"
                  style={{ background: bgGradient }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-semibold text-sm text-slate-900 [text-shadow:0_1px_1px_rgba(255,255,255,0.6)] dark:text-white dark:[text-shadow:none]">
                          {displayName}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-800/90 dark:text-white/70">
                          <Package className="h-3 w-3" />
                          <span>
                            {investment.amount.toFixed(2)} {investment.currency}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {summary && (
        <div className="grid gap-4 md:grid-cols-2">
          <article className="rounded-3xl border border-border bg-muted/30 p-4 dark:border-white/10 dark:bg-white/5">
            <h3 className="text-lg font-semibold">
              {t("dashboard.topPositions")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground dark:text-white/70">
              {summary.allocations.slice(0, 3).map((allocation) => (
                <li
                  key={allocation.assetSymbol}
                  className="flex items-center justify-between rounded-2xl border border-border bg-muted/50 px-3 py-2 dark:border-white/10 dark:bg-black/20">
                  <span>
                    {allocation.assetName} ({allocation.assetSymbol})
                  </span>
                  <span className="font-semibold text-foreground dark:text-white">
                    {allocation.percentage}%
                  </span>
                </li>
              ))}
            </ul>
          </article>
          <article className="rounded-3xl border border-border bg-muted/30 p-4 dark:border-white/10 dark:bg-white/5">
            <h3 className="text-lg font-semibold">
              {t("dashboard.quickAnalytics")}
            </h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-muted/50 p-3 dark:border-white/10 dark:bg-black/20">
                <p className="text-xs text-muted-foreground dark:text-white/60">
                  {t("dashboard.volatility")}
                </p>
                <p className="text-2xl font-semibold text-amber-800 dark:text-amber-300">1.8%</p>
              </div>
              <div className="rounded-2xl border border-border bg-muted/50 p-3 dark:border-white/10 dark:bg-black/20">
                <p className="text-xs text-muted-foreground dark:text-white/60">
                  {t("dashboard.assetsCount")}
                </p>
                <p className="text-2xl font-semibold text-foreground dark:text-white">
                  {summary.allocations.length}
                </p>
              </div>
            </div>
          </article>
        </div>
      )}
    </section>
  );
};

export default DashboardPage;
